import { dxtToLocalServerTime, getMomentTz } from "@dexteel/mesf-core";
import {
  Comment as CommentIcon,
  CommentOutlined as EmptyCommentIcon,
  Warning as WarningIcon,
} from "@mui/icons-material";
import { Box, ButtonBase, Tooltip, Typography } from "@mui/material";
import type { Strand } from "../../../models/caster.models";

interface StrandLossDetailsCellProps {
  strand?: Strand;
  onOpenLossDetails: (strand: Strand) => void;
}

const REQUIRED_REASON_LABEL = "Reason required";
const UNAVAILABLE_VALUE = "-";
const DATE_TIME_FORMAT = "MM/dd/yy HH:mm:ss";
const DURATION_FORMAT = "d[d] h[h] m[m] s[s]";
const moment = getMomentTz();

export const StrandLossDetailsCell = ({
  strand,
  onOpenLossDetails,
}: StrandLossDetailsCellProps) => {
  if (!strand || strand.StrandLossId === null) return null;

  const reasonLabel =
    strand.StrandLossCodeName?.trim() || REQUIRED_REASON_LABEL;
  const hasReason = !!strand.StrandLossCodeName?.trim();
  const hasComment = !!strand.StrandLossComments?.trim();
  const disabled =
    strand.StrandsParentAssetId === null || strand.StrandAssetId === null;
  const tooltipReason = strand.StrandLossCodeName?.trim() || UNAVAILABLE_VALUE;
  const tooltipComments =
    strand.StrandLossComments?.trim() || UNAVAILABLE_VALUE;
  const tooltipDuration =
    strand.StrandLossDuration === null
      ? UNAVAILABLE_VALUE
      : moment
          .duration(strand.StrandLossDuration, "seconds")
          .format(DURATION_FORMAT);

  return (
    <Tooltip
      title={
        <Box>
          {strand.StrandLossIsManual && (
            <Typography variant="caption" display="block">
              Manually Generated Loss
            </Typography>
          )}
          <Typography variant="subtitle2">Strand {strand.Strand}</Typography>
          <Typography variant="caption" display="block">
            {`Start: ${dxtToLocalServerTime(strand.StrandLossStart, DATE_TIME_FORMAT) || UNAVAILABLE_VALUE}`}
          </Typography>
          <Typography variant="caption" display="block">
            {`End: ${dxtToLocalServerTime(strand.StrandLossEnd, DATE_TIME_FORMAT) || UNAVAILABLE_VALUE}`}
          </Typography>
          <Typography variant="caption" display="block">
            Duration: {tooltipDuration}
          </Typography>
          <Typography variant="caption" display="block">
            Reason: {tooltipReason}
          </Typography>
          <Typography variant="caption" display="block">
            Comments: {tooltipComments}
          </Typography>
        </Box>
      }
    >
      <Box component="span" sx={{ display: "block", width: "100%" }}>
        <ButtonBase
          disabled={disabled}
          onClick={() => onOpenLossDetails(strand)}
          sx={(theme) => ({
            alignItems: "center",
            borderRadius: 0.5,
            display: "flex",
            gap: 0.75,
            justifyContent: "flex-start",
            minWidth: 0,
            opacity: disabled ? theme.palette.action.disabledOpacity : 1,
            px: 0.75,
            py: 0.25,
            textAlign: "left",
            width: "100%",
            "&.Mui-focusVisible": {
              outline: `2px solid ${theme.palette.primary.main}`,
              outlineOffset: 1,
            },
          })}
        >
          {!hasReason ? (
            <WarningIcon color="warning" fontSize="small" aria-hidden />
          ) : null}
          <Box sx={{ minWidth: 0, overflow: "hidden", flex: 1 }}>
            <Typography
              component="span"
              variant="caption"
              noWrap
              sx={{
                color: hasReason ? "text.primary" : "warning.main",
                display: "block",
                fontWeight: 600,
                lineHeight: 1.15,
                textAlign: "center",
              }}
            >
              {reasonLabel}
            </Typography>
          </Box>
          {hasComment ? (
            <CommentIcon color="primary" fontSize="small" aria-hidden />
          ) : (
            <EmptyCommentIcon color="disabled" fontSize="small" aria-hidden />
          )}
        </ButtonBase>
      </Box>
    </Tooltip>
  );
};
